import { Search, Bell, ChevronDown, LogOut, Command, Handshake, Clock, UserPlus, CalendarClock, CheckCircle2, PanelLeftClose, PanelLeftOpen, Loader2, AtSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi, settingsApi, type ApiNotification } from "@/lib/api";

type NotificationType = "deal_stage" | "task_overdue" | "lead_assigned" | "meeting_reminder" | "mention";

function relativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  return `${days}d ago`;
}

function isToday(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
}

const typeConfig: Record<string, { icon: typeof Handshake; bg: string; text: string }> = {
  deal_stage:        { icon: Handshake,    bg: "bg-blue-500/10",   text: "text-blue-500" },
  task_overdue:      { icon: Clock,        bg: "bg-orange-500/10", text: "text-orange-500" },
  lead_assigned:     { icon: UserPlus,     bg: "bg-green-500/10",  text: "text-green-500" },
  meeting_reminder:  { icon: CalendarClock,bg: "bg-purple-500/10", text: "text-purple-500" },
  mention:           { icon: AtSign,       bg: "bg-teal-500/10",   text: "text-teal-600" },
  _default:          { icon: Bell,         bg: "bg-muted",         text: "text-muted-foreground" },
};

interface AppHeaderProps {
  onOpenCommandPalette?: () => void;
  onToggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
}

export function AppHeader({ onOpenCommandPalette, onToggleSidebar, sidebarCollapsed }: AppHeaderProps) {
  const navigate = useNavigate();
  const { currentUser, logout, apiUser } = useAuth();
  const qc = useQueryClient();

  // Fetch real user profile (first/last name, role) from /me
  const { data: meData } = useQuery({
    queryKey: ["header-me"],
    queryFn: () => settingsApi.getMe(),
    staleTime: 5 * 60_000,
  });
  const me = meData?.user;
  const fullName = me ? `${me.firstName} ${me.lastName}`.trim() : currentUser?.name ?? "User";
  const userRole = me?.role ?? currentUser?.role ?? "";

  // ── Fetch notifications — poll every 30 s ────────────────────────
  const { data: notifData, isLoading: notifLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationsApi.list(),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  // Defend against a non-array payload (e.g. an undecrypted envelope object
  // slipping through when payload-crypto isn't ready) so the header never crashes.
  const notifications: ApiNotification[] = Array.isArray(notifData) ? notifData : [];

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const todayNotifs    = notifications.filter((n) => isToday(n.createdAt));
  const earlierNotifs  = notifications.filter((n) => !isToday(n.createdAt));

  const markRead = async (id: string) => {
    await notificationsApi.markRead(id).catch(() => {});
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const markAllRead = async () => {
    await notificationsApi.markAllRead().catch(() => {});
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const handleNotifClick = (notif: ApiNotification) => {
    markRead(notif.id);
    if (notif.link) navigate(notif.link);
  };

  const initials = fullName
    .split(" ").map((n) => n[0]).filter(Boolean).join("").slice(0, 2).toUpperCase() || "?";

  const renderNotifRow = (notif: ApiNotification) => {
    const cfg = typeConfig[notif.type] ?? typeConfig._default;
    const Icon = cfg.icon;
    return (
      <div
        key={notif.id}
        className="flex items-start gap-3 p-3 border-b border-border/60 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => handleNotifClick(notif)}
      >
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${cfg.bg}`}>
          <Icon className={`h-4 w-4 ${cfg.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-foreground leading-snug">{notif.title}</p>
          <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">{notif.message}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{relativeTime(notif.createdAt)}</p>
        </div>
        {!notif.isRead && (
          <div className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
        )}
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-30 flex h-12 items-center justify-between border-b border-border bg-card/80 backdrop-blur-md px-3 md:px-4">
      <div className="flex items-center gap-2 md:gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onToggleSidebar}
          title={sidebarCollapsed ? "Expand sidebar (Ctrl+B)" : "Collapse sidebar (Ctrl+B)"}
        >
          {sidebarCollapsed
            ? <PanelLeftOpen className="h-4 w-4 text-muted-foreground" />
            : <PanelLeftClose className="h-4 w-4 text-muted-foreground" />
          }
        </Button>
        <button
          type="button"
          onClick={onOpenCommandPalette}
          className="relative w-8 md:w-64 flex items-center h-8 pl-2 md:pl-8 pr-2 md:pr-8 text-[13px] bg-muted/40 border border-border/60 rounded-md text-muted-foreground hover:bg-muted/60 transition-colors cursor-pointer"
        >
          <Search className="h-3.5 w-3.5 text-muted-foreground md:absolute md:left-2.5 md:top-1/2 md:-translate-y-1/2" />
          <span className="hidden md:inline ml-1">Search…</span>
          <kbd className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none hidden md:inline-flex h-5 select-none items-center gap-0.5 rounded border border-border/60 bg-muted/60 px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            <Command className="h-2.5 w-2.5" />K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-1 md:gap-1.5">
        {/* Notifications */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative h-8 w-8">
              {notifLoading
                ? <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                : <Bell className="h-4 w-4 text-muted-foreground" />}
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-medium text-destructive-foreground">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] md:w-[360px] p-0" align="end">
            <div className="flex items-center justify-between p-3 border-b border-border">
              <p className="text-[13px] font-semibold text-foreground">
                Notifications {unreadCount > 0 && (
                  <Badge variant="secondary" className="ml-1.5 text-[10px]">{unreadCount} new</Badge>
                )}
              </p>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] text-primary hover:underline cursor-pointer"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <CheckCircle2 className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-[13px] text-muted-foreground">You're all caught up</p>
                  <p className="text-[11px] text-muted-foreground/70 mt-1">No notifications yet</p>
                </div>
              ) : (
                <>
                  {todayNotifs.length > 0 && (
                    <>
                      <p className="px-3 pt-2 pb-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Today</p>
                      {todayNotifs.map(renderNotifRow)}
                    </>
                  )}
                  {earlierNotifs.length > 0 && (
                    <>
                      <p className="px-3 pt-2 pb-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Earlier</p>
                      {earlierNotifs.map(renderNotifRow)}
                    </>
                  )}
                  {notifications.every((n) => n.isRead) && (
                    <div className="flex items-center justify-center gap-1.5 py-3 text-[11px] text-muted-foreground">
                      <CheckCircle2 className="h-3.5 w-3.5" /> All caught up
                    </div>
                  )}
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 gap-1.5 px-1.5 hover:bg-muted/60 max-w-[200px]">
              <Avatar className="h-6 w-6 shrink-0">
                <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start min-w-0">
                <span className="text-[12px] font-semibold leading-tight truncate max-w-[120px]">{fullName}</span>
                <span className="text-[10px] text-muted-foreground leading-tight truncate max-w-[120px]">{userRole}</span>
              </div>
              <ChevronDown className="h-3 w-3 text-muted-foreground hidden md:block shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="font-normal py-2">
              <div className="flex items-center gap-2.5">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary/10 text-primary text-[11px] font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <p className="text-[13px] font-semibold truncate">{fullName}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{apiUser?.email ?? ""}</p>
                  <Badge variant="secondary" className="text-[9px] py-0 px-1.5 w-fit mt-0.5">{userRole}</Badge>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-[13px]" onClick={() => navigate("/settings")}>Profile</DropdownMenuItem>
            <DropdownMenuItem className="text-[13px]" onClick={() => navigate("/settings")}>Preferences</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-[13px] text-destructive" onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
